import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { can } from "../lib/api";
import { useQuick } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Search, Plus, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Patients() {
  const { openModal } = useQuick();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [patients, setPatients] = useState([]);
  const timer = useRef(null);

  const load = useCallback(async (query = "") => {
    try {
      const { data } = await api.get("/patients", { params: { q: query } });
      setPatients(data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load(q);
    window.addEventListener("ll:data-changed", h);
    return () => window.removeEventListener("ll:data-changed", h);
  }, [load, q]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(q), 250);
    return () => clearTimeout(timer.current);
  }, [q, load]);

  const del = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare il paziente ${p.first_name} ${p.last_name}? (soft-delete, i dati restano in archivio)`)) return;
    try {
      await api.delete(`/patients/${p.id}`);
      toast.success("Paziente eliminato");
      load(q);
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  return (
    <div className="space-y-4" data-testid="patients-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold">Pazienti</h1>
        <div className="relative flex-1 max-w-md ml-auto">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="patients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, telefono, email..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm" />
        </div>
        {can(user, "pazienti", "create") && <button data-testid="patients-new" onClick={() => openModal("patient")} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700"><Plus size={16} /> Paziente</button>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="patients-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="py-3 px-4">Paziente</th><th className="px-3">Telefono</th><th className="px-3 hidden md:table-cell">Email</th>
                <th className="px-3 hidden lg:table-cell">Nascita</th><th className="px-3 hidden lg:table-cell">Note operative</th><th className="px-3"></th>
              </tr>
            </thead>
            <tbody>
              {patients.map((p) => (
                <tr key={p.id} data-testid={`patient-row-${p.id}`} onClick={() => navigate(`/pazienti/${p.id}`)} className="border-b border-slate-50 hover:bg-blue-50/40 cursor-pointer transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                        {p.first_name?.[0]}{p.last_name?.[0]}
                      </span>
                      <span className="font-medium">{p.first_name} {p.last_name}</span>
                    </div>
                  </td>
                  <td className="px-3 text-slate-600"><span className="flex items-center gap-1"><Phone size={12} className="text-slate-300" />{p.phone || "—"}</span></td>
                  <td className="px-3 text-slate-600 hidden md:table-cell">{p.email || "—"}</td>
                  <td className="px-3 text-slate-600 hidden lg:table-cell">{p.birth_date ? p.birth_date.split("-").reverse().join("/") : "—"}</td>
                  <td className="px-3 text-slate-400 text-xs max-w-48 truncate hidden lg:table-cell">{p.op_notes || ""}</td>
                  <td className="px-3">
                    {can(user, "pazienti", "delete") && (
                      <button data-testid={`patient-delete-${p.id}`} onClick={(e) => del(e, p)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {patients.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-slate-400" data-testid="patients-empty">Nessun paziente trovato</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
